import { useMutation } from "@tanstack/react-query";
import { getShopifyShopId } from "@/utils/metadata";
import { getVisitorId } from "@/utils/tracking";

const API_BASE_URL =
  import.meta.env.VITE_API_URL || "https://api.socialocean.co/api";

export interface ToggleLikeResult {
  liked: boolean;
  likes_count: number;
}

/**
 * Toggle the current visitor's like on a review. The endpoint is a single
 * toggle — the first call likes, the next un-likes (no separate unlike route).
 * The response carries the authoritative `liked` + `likes_count`, which callers
 * use to reconcile their optimistic UI.
 */
export function useToggleReviewLike() {
  return useMutation({
    mutationFn: async (reviewId: number): Promise<ToggleLikeResult> => {
      const shop = getShopifyShopId();
      const visitorId = getVisitorId();

      const response = await fetch(
        `${API_BASE_URL}/widget/reviews/${reviewId}/like`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true",
          },
          credentials: "omit",
          body: JSON.stringify({ shop, visitorId }),
        },
      );

      if (!response.ok) {
        throw new Error(`Review like toggle failed: ${response.status}`);
      }

      const json = await response.json();
      const d = json.data ?? json;
      return { liked: Boolean(d.liked), likes_count: d.likes_count ?? 0 };
    },
  });
}
